import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import PayDialog from "../../components/PayDialog";
import { formatPrice } from "../../lib/utils";

export default function GiftCards() {
  const [cards, setCards] = useState([]);
  const [amount, setAmount] = useState(10000);
  const [showForm, setShowForm] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/gift-cards").then(({ data }) => setCards(data)).catch(() => setCards([]));

  useEffect(() => {
    load();
  }, []);

  const buy = (e) => {
    e.preventDefault();
    setPaying(true);
  };

  return (
    <div>
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Gift cards</h1>

      <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
        SHUWA Special Offers
      </h2>
      <button
        type="button"
        onClick={() => setShowForm((v) => !v)}
        className="relative mb-6 block w-full max-w-sm overflow-hidden rounded-2xl bg-slate-900 p-5 text-left text-white shadow-sm transition hover:shadow-md"
      >
        <div className="mb-6 flex items-center justify-between">
          <span className="flex items-center gap-2 font-montserrat text-lg font-extrabold">
            <span className="brand-gradient flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold">
              S
            </span>
            SHUWA
          </span>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Value</p>
            <p className="text-sm font-bold">From {formatPrice(1000)}</p>
          </div>
        </div>
        <p className="font-montserrat text-xl font-bold">Gift card</p>
        <p className="mb-6 text-sm text-slate-300">Valid for all services</p>
        <div className="flex items-end justify-between">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white">
            <Gift className="h-3.5 w-3.5" /> Buy card now
          </span>
          <p className="text-right text-xs text-slate-400">
            Expires after
            <br />
            12 months
          </p>
        </div>
      </button>

      {showForm && (
        <Card className="mb-6 p-4">
          <form onSubmit={buy} className="flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Amount (RWF)</label>
              <Input type="number" min={1000} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <Button type="submit" disabled={Number(amount) < 1000}>Continue to payment</Button>
          </form>
        </Card>
      )}

      {paying && (
        <PayDialog
          title="Gift card"
          amount={Number(amount)}
          url="/gift-cards"
          body={{ amount: Number(amount) }}
          allowCash={false}
          onClose={() => setPaying(false)}
          onPaid={() => {
            setPaying(false);
            setShowForm(false);
            load();
          }}
        />
      )}

      {cards.length === 0 ? (
        <p className="text-muted-foreground">You don't have any gift cards yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {cards.map((c) => (
            <Card key={c.id} className="p-4">
              <div className="flex items-center justify-between">
                <p className="font-mono text-sm text-slate-900">{c.code}</p>
                <Badge variant={c.status === "active" ? "accent" : "default"}>{c.status === "pending" ? "Awaiting payment" : c.status}</Badge>
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900">{formatPrice(c.remaining_amount)}</p>
              <p className="text-xs text-muted-foreground">of {formatPrice(c.original_amount)} original</p>
              {c.status === "active" && (
                <p className="mt-2 text-xs text-muted-foreground">
                  To pay, show this code at {c.business?.name || "any SHUWA business"}. Keep it private — it works like cash.
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
